import { Colors, Label, MultiSelectFilled, TextInput } from '@amelio/ui-library';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { AspirationFollowUp, AspirationQuestionBlock } from './AspirationQuestionBlock';
import { GROWTH_FIELD_LABEL } from './careerAspirationStyles';
import { SHORT_NOTES_MAX_LENGTH } from './careerAspirationValidation';
import { useCareerAspirationFormText } from './useCareerAspirationFormText';
import { useSupportNeedsChoice } from './useSupportNeedsChoice';

import type { CareerAspirationFormValues } from './careerAspirationValidation';
import type { CareerAspirationQuestionProps } from './types';

/** The support the employee would need, picked from a list, with "Other" written out. */
export const SupportNeedsQuestion = ({
    employeeFirstName,
    dataTestId,
}: CareerAspirationQuestionProps) => {
    const { t, errorMessage } = useCareerAspirationFormText();
    const fieldId = useId();
    const {
        control,
        formState: { errors },
    } = useFormContext<CareerAspirationFormValues>();
    const { groups, selected, hasOtherSupport, toggle } = useSupportNeedsChoice();

    const question = t('performanceProfile.growth.aspiration.supportNeeds', {
        name: employeeFirstName,
    });

    return (
        <AspirationQuestionBlock
            title={question}
            dataTestId={`${dataTestId}-support-needs`}
        >
            <MultiSelectFilled
                groups={groups}
                values={selected}
                onChange={toggle}
                sort={'none'}
                searchable
                searchPlaceholder={t('performanceProfile.growth.aspiration.form.searchSupport')}
                placeholder={t('performanceProfile.growth.aspiration.form.supportNeedsPlaceholder')}
                showSelection
                variant={'white'}
                // The picked needs are answers, not a highlight: they wear the library's neutral
                // accent so the brand turquoise stays for what the manager is meant to act on.
                accent={'neutral'}
                ariaLabel={question}
                emptyText={t('common.noResults')}
                dataTestId={`${dataTestId}-support-select`}
            />

            {hasOtherSupport && (
                <AspirationFollowUp
                    label={
                        <Label
                            htmlFor={`${fieldId}-support-other`}
                            color={Colors.NAVY}
                            className={GROWTH_FIELD_LABEL}
                        >
                            {t('performanceProfile.growth.aspiration.form.supportNeedsOther')}
                        </Label>
                    }
                >
                    <Controller
                        name={'supportNeedsOther'}
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                ref={field.ref}
                                id={`${fieldId}-support-other`}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                placeholder={t(
                                    'performanceProfile.growth.aspiration.form.supportNeedsOtherPlaceholder',
                                )}
                                maxLength={SHORT_NOTES_MAX_LENGTH}
                                variant={'white'}
                                bordered
                                error={!!errors.supportNeedsOther}
                                errorMessage={errorMessage(errors.supportNeedsOther?.message)}
                                dataTestId={`${dataTestId}-support-other`}
                            />
                        )}
                    />
                </AspirationFollowUp>
            )}
        </AspirationQuestionBlock>
    );
};
